import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  FileText, Loader2, AlertCircle, CheckCircle2, Clock, RefreshCw, Download,
  MessageSquare, Send, X, ChevronLeft, Lock, ArrowRight,
} from 'lucide-react';
import ExecutionPanel from '../vdr/ExecutionPanel';
import SignaturePanel from '../vdr/SignaturePanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDateTime = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return '—'; }
};

const StatusPill = ({ status }) => {
  const cfg = { draft: 'bg-gray-600/30 text-gray-300', shared: 'bg-blue-500/20 text-blue-300', final: 'bg-[#059669]/20 text-[#059669]' }[status] || 'bg-gray-600/30 text-gray-300';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg}`}>{status || '—'}</span>;
};

const responseLabel = (s) => ({
  approved: { icon: CheckCircle2, color: 'text-[#059669]', label: 'You approved' },
  changes_requested: { icon: RefreshCw, color: 'text-orange-400', label: 'Changes requested' },
  pending: { icon: Clock, color: 'text-amber-300', label: 'Action required' },
}[s] || { icon: Clock, color: 'text-gray-400', label: '—' });

// Subtle "Powered by StartEase Agent" footer for the Data Room section
const BrandingFooter = () => (
  <div
    className="mt-6 pt-4 border-t border-gray-800/80 flex flex-col sm:flex-row items-center justify-between gap-1 text-[11px] text-gray-500"
    data-testid="client-vdr-branding-footer"
  >
    <p className="tracking-wide">
      Powered by{' '}
      <a
        href="https://starteaseai.com"
        target="_blank"
        rel="noopener noreferrer"
        className="text-gray-400 hover:text-[#059669] transition-colors"
        data-testid="client-vdr-branding-link"
      >
        StartEase Agent
      </a>
      {' · US entity, tax & compliance — done for you'}
    </p>
    <p className="text-gray-600">starteaseai.com</p>
  </div>
);

// Post-approval marketing nudge (only after `approved`)
const PostApprovalNudge = () => (
  <div
    className="mt-4 bg-[#0b1310] border border-[#059669]/30 rounded-lg p-4"
    data-testid="client-post-approval-nudge"
  >
    <p className="text-sm font-semibold text-white">Need to form your own US company?</p>
    <p className="text-xs text-gray-400 mt-1 leading-relaxed">
      StartEase handles everything — LLC, C-Corp, EIN, Compliance.
    </p>
    <a
      href="https://starteaseai.com"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 bg-[#059669]/15 hover:bg-[#059669]/25 text-[#059669] rounded-md text-xs font-semibold transition-colors"
      data-testid="client-post-approval-nudge-cta"
    >
      Learn More <ArrowRight className="w-3.5 h-3.5" />
    </a>
  </div>
);

// Render reviewer instructions as a bullet list when multiple non-empty lines
// are present; fall back to plain text for a single-line message.
const ReviewerInstructionsBox = ({ text, testId }) => {
  const lines = (text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length === 0) return null;
  return (
    <div
      className="mt-3 bg-amber-500/10 border-l-4 border-amber-400 rounded-lg p-3 flex items-start gap-2.5"
      data-testid={testId}
    >
      <div className="text-amber-300 mt-0.5 shrink-0" aria-hidden="true">ℹ️</div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-amber-300 uppercase tracking-wide mb-1">
          Instructions from StartEase
        </p>
        {lines.length === 1 ? (
          <p className="text-sm text-amber-100">{lines[0]}</p>
        ) : (
          <ul className="text-sm text-amber-100 space-y-1 list-disc pl-5">
            {lines.map((l, i) => <li key={i}>{l}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
};

const WhatsNewBanner = ({ note, versionNumber, testId }) => {
  const trimmed = (note || '').trim();
  if (!trimmed) return null;
  return (
    <div
      className="mt-3 bg-amber-500/10 border-l-4 border-amber-400 rounded-lg p-3 flex items-start gap-2.5"
      data-testid={testId}
    >
      <div className="text-amber-300 mt-0.5 shrink-0" aria-hidden="true">✨</div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-amber-300 uppercase tracking-wide mb-1">
          What&apos;s new in v{versionNumber}
        </p>
        <p className="text-sm text-amber-100 whitespace-pre-wrap">{trimmed}</p>
      </div>
    </div>
  );
};

const DataRoomDetailPanel = ({ companyId, docId, token, onClose, onChanged }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showChanges, setShowChanges] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionErr, setActionErr] = useState('');
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/client/vdr/${companyId}/documents/${docId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (active) setDoc(r.data);
      } catch (e) {
        if (active) setError(e.response?.data?.detail || 'Failed to load');
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [docId, companyId, token]);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/client/vdr/${companyId}/documents/${docId}`, { headers: { Authorization: `Bearer ${token}` } });
      setDoc(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Failed to load'); }
  };

  const yours = doc?.your_approval;
  const locked = yours && yours.status !== 'pending';

  const submit = async (status, comment_text) => {
    setSubmitting(true); setActionErr('');
    try {
      await axios.post(`${API}/client/vdr/${companyId}/documents/${docId}/approve`,
        { status, comment_text: comment_text || null },
        { headers: { Authorization: `Bearer ${token}` } });
      setShowChanges(false); setReason('');
      await refresh(); onChanged?.();
    } catch (e) {
      setActionErr(e.response?.data?.detail || 'Could not submit your response.');
    } finally { setSubmitting(false); }
  };

  const openFresh = async () => {
    try {
      const r = await axios.get(`${API}/client/vdr/${companyId}/documents/${docId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setDoc(r.data);
      const url = r.data?.current_version?.presigned_url;
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) { /* swallow — the panel reload error is enough signal */ }
  };

  const postComment = async () => {
    if (!newComment.trim() || posting) return;
    setPosting(true);
    try {
      await axios.post(`${API}/client/vdr/${companyId}/documents/${docId}/comments`,
        { comment_text: newComment.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      setNewComment(''); await refresh();
    } finally { setPosting(false); }
  };

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-[640px] bg-[#0b1310] border-l border-gray-800 shadow-2xl z-40 overflow-y-auto" data-testid="client-doc-detail-panel">
      <div className="sticky top-0 bg-[#0b1310] border-b border-gray-800 px-5 py-3 flex items-center justify-between z-10">
        <button onClick={onClose} className="text-gray-400 hover:text-white flex items-center gap-1.5 text-sm">
          <ChevronLeft className="w-4 h-4" /> Back to Data Room
        </button>
        <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-6">
        {loading ? <div className="flex items-center gap-2 text-gray-400 py-8"><Loader2 className="w-5 h-5 animate-spin" /> Loading...</div>
         : error ? <div className="text-red-400 text-sm">{error}</div>
         : doc && (
          <>
            <h2 className="text-xl font-semibold text-white">{doc.title}</h2>
            {doc.description && <p className="text-sm text-gray-400 mt-1">{doc.description}</p>}
            <WhatsNewBanner note={doc.current_change_note} versionNumber={doc.current_version_number} testId="client-whats-new" />
            <ReviewerInstructionsBox text={doc.reviewer_instructions} testId="client-reviewer-instructions" />
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <StatusPill status={doc.status} />
              <span className="text-xs text-gray-300">Version {doc.current_version_number}</span>
              {doc.current_version && (
                <button type="button" onClick={openFresh}
                   className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#059669]/15 hover:bg-[#059669]/25 text-[#059669] rounded-lg text-xs font-medium"
                   data-testid="client-doc-download">
                  <Download className="w-3.5 h-3.5" /> Download PDF
                </button>
              )}
            </div>

            {/* Execute Agreement panel (also surfaces downloads once executed) */}
            <div className="mt-5">
              <SignaturePanel
                doc={doc}
                docEndpointBase={`${API}/client/vdr/${companyId}/documents/${docId}`}
                authHeaders={{ Authorization: `Bearer ${token}` }}
                onRefresh={refresh}
                testIdPrefix="client"
              />
              <ExecutionPanel
                doc={doc}
                docEndpointBase={`${API}/client/vdr/${companyId}/documents/${docId}`}
                authHeaders={{ Authorization: `Bearer ${token}` }}
                onRefresh={refresh}
                testIdPrefix="client"
                canExecute={true}
              />
            </div>

            {/* Your response card */}
            <div className="mt-5 bg-[#111827] border border-gray-800 rounded-xl p-4">
              <h3 className="text-sm font-medium text-white mb-3">Your response — Version {doc.current_version_number}</h3>
              {!yours ? (
                <p className="text-xs text-gray-500">You&apos;re not listed as a reviewer on this version.</p>
              ) : locked ? (
                <div data-testid="client-locked-state">
                  {yours.status === 'approved' ? (
                    <>
                      <div className="flex items-center gap-3 text-[#059669]">
                        <CheckCircle2 className="w-5 h-5" />
                        <div>
                          <p className="text-sm font-semibold">You approved this version</p>
                          <p className="text-xs text-gray-400">{fmtDateTime(yours.responded_at)}</p>
                        </div>
                      </div>
                      <PostApprovalNudge />
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 text-orange-400">
                        <RefreshCw className="w-5 h-5" />
                        <div>
                          <p className="text-sm font-semibold">You requested changes</p>
                          <p className="text-xs text-gray-400">{fmtDateTime(yours.responded_at)}</p>
                        </div>
                      </div>
                      {yours.comment_text && (
                        <div className="mt-2 text-xs text-orange-300 bg-orange-500/10 rounded p-2.5 italic">&ldquo;{yours.comment_text}&rdquo;</div>
                      )}
                    </>
                  )}
                  <p className="text-xs text-gray-500 mt-3 flex items-center gap-1"><Lock className="w-3 h-3" /> Locked. A new version will reset all approvals.</p>
                </div>
              ) : showChanges ? (
                <div data-testid="client-changes-form">
                  <p className="text-sm text-gray-300 mb-2">Tell the team what needs to change:</p>
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} autoFocus
                            placeholder="Please describe what needs to be updated..."
                            className="w-full bg-[#0b1310] border border-gray-700 rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none" />
                  {actionErr && <div className="text-xs text-red-400 mt-2">{actionErr}</div>}
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => { setShowChanges(false); setReason(''); }} className="flex-1 py-2 text-sm text-gray-300 hover:text-white">Cancel</button>
                    <button onClick={() => submit('changes_requested', reason.trim())} disabled={!reason.trim() || submitting}
                            className="flex-1 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                      {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Submit
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {actionErr && <div className="text-xs text-red-400">{actionErr}</div>}
                  <button onClick={() => submit('approved')} disabled={submitting}
                          className="w-full py-2 bg-[#059669] hover:bg-[#00d96e] text-black rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                          data-testid="client-approve-btn">
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    <CheckCircle2 className="w-4 h-4" /> Approve
                  </button>
                  <button onClick={() => setShowChanges(true)}
                          className="w-full py-2 bg-white/5 hover:bg-white/10 border border-orange-400/40 text-orange-300 rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
                    <RefreshCw className="w-4 h-4" /> Request Changes
                  </button>
                </div>
              )}
            </div>

            {/* Activity */}
            <div className="mt-5 bg-[#111827] border border-gray-800 rounded-xl p-4">
              <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-1.5"><MessageSquare className="w-4 h-4" /> Discussion</h3>
              <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                {(doc.activity || []).length === 0 && <p className="text-xs text-gray-500">No activity yet.</p>}
                {(doc.activity || []).map((e) => {
                  const isComment = e.entry_type === 'comment';
                  if (isComment) {
                    return (
                      <div key={e.id} className="border-l-2 border-[#059669]/30 pl-3">
                        <div className="text-xs text-gray-400">
                          <span className="text-white font-medium">{e.performed_by_name || e.performed_by_email}</span>
                          {' · '}{fmtDateTime(e.created_at)}{' · '}<span className="font-mono">v{e.version_number}</span>
                        </div>
                        <p className="text-sm text-gray-100 mt-1 whitespace-pre-wrap">{e.comment_text}</p>
                      </div>
                    );
                  }
                  const isReset = e.action === 'approvals_reset';
                  const isVer = e.action === 'new_version_uploaded';
                  return (
                    <div key={e.id} className="text-xs text-gray-500 italic">
                      <span className="font-mono not-italic mr-1.5">v{e.version_number}</span>
                      {isReset ? '── All approvals reset ──'
                       : isVer ? `Version ${e.version_number} uploaded · ${fmtDateTime(e.created_at)}`
                       : `${e.action} · ${fmtDateTime(e.created_at)}`}
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 flex gap-2">
                <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} rows={2}
                          placeholder="Add a comment..."
                          className="flex-1 bg-[#0b1310] border border-gray-700 rounded-lg px-3 py-2 text-sm focus:border-[#059669] focus:outline-none" />
                <button onClick={postComment} disabled={!newComment.trim() || posting}
                        className="self-end px-3 py-2 bg-[#059669] hover:bg-[#00d96e] text-black rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center gap-1.5">
                  {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Post
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ClientDataRoomSection = ({ companyId, token }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const r = await axios.get(`${API}/client/vdr/${companyId}/documents`, { headers: { Authorization: `Bearer ${token}` } });
        if (active) setDocs(r.data || []);
      } catch (e) {
        if (active) {
          if (e.response?.status === 403) setDocs([]);
          else setError(e.response?.data?.detail || 'Failed to load Data Room');
        }
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [companyId, token]);

  const fetchDocs = async () => {
    try {
      const r = await axios.get(`${API}/client/vdr/${companyId}/documents`, { headers: { Authorization: `Bearer ${token}` } });
      setDocs(r.data || []);
    } catch (e) {
      if (e.response?.status === 403) setDocs([]);
      else setError(e.response?.data?.detail || 'Failed to load Data Room');
    }
  };

  const pending = useMemo(() => docs.filter((d) => d.your_approval_status === 'pending').length, [docs]);

  return (
    <section id="data-room" className="bg-[#111827] border border-gray-800 rounded-lg p-6 scroll-mt-20" data-testid="client-data-room-section">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#059669]" /> Data Room
          </h2>
          <p className="text-sm text-gray-400 mt-0.5">Documents shared with you and your team</p>
        </div>
        {pending > 0 && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-300 text-xs font-medium">
            <Clock className="w-3 h-3" /> {pending} awaiting your response
          </span>
        )}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 py-8 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading...</div>
      ) : error ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-red-400 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      ) : docs.length === 0 ? (
        <div className="py-10 text-center" data-testid="client-data-room-empty">
          <FileText className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-300 text-sm font-medium">No documents yet.</p>
          <p className="text-gray-500 text-xs mt-1">Your team will see documents here once they&apos;re shared with the Data Room.</p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-700/60">
              <tr>
                <th className="text-left px-2 py-2 text-gray-400 font-medium text-xs uppercase tracking-wide">Document</th>
                <th className="text-left px-2 py-2 text-gray-400 font-medium text-xs uppercase tracking-wide">Version</th>
                <th className="text-left px-2 py-2 text-gray-400 font-medium text-xs uppercase tracking-wide">Status</th>
                <th className="text-left px-2 py-2 text-gray-400 font-medium text-xs uppercase tracking-wide">Your response</th>
                <th className="text-right px-2 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/80">
              {docs.map((d) => {
                const r = responseLabel(d.your_approval_status);
                const Icon = r.icon;
                return (
                  <tr key={d.id} className="hover:bg-white/5 cursor-pointer" onClick={() => setOpenId(d.id)}
                      data-testid={`client-data-room-row-${d.id}`}>
                    <td className="px-2 py-3 text-white">{d.title}</td>
                    <td className="px-2 py-3 text-gray-300 text-xs font-mono">v{d.current_version_number}</td>
                    <td className="px-2 py-3"><StatusPill status={d.status} /></td>
                    <td className={`px-2 py-3 inline-flex items-center gap-1.5 ${r.color}`}>
                      <Icon className="w-4 h-4" /><span className="text-xs font-medium">{r.label}</span>
                    </td>
                    <td className="px-2 py-3 text-right text-[#059669] text-xs font-medium">Open →</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openId && <DataRoomDetailPanel companyId={companyId} docId={openId} token={token}
                                       onClose={() => setOpenId(null)}
                                       onChanged={fetchDocs} />}
      <BrandingFooter />
    </section>
  );
};

export default ClientDataRoomSection;
